import mapboxgl, { Map } from '!mapbox-gl';
import area from '@turf/area';

import { THealthSecurityIncident } from '@types';

import { TSelectedCountry } from '@components';
import {
    STEP_STYLE,
    MAPBOX_BASEMAP_STYLE,
    INCIDENTS_ICON,
    DATA_VALUES,
    LAYER_NAMES,
    LAYER_SOURCE_NAMES,
} from '@constants';
import {
    TIncidentsGeoJson,
    TCountriesGeoJSON,
} from './global-incidents-map.types';

import { defaultTheme } from '../../styles';

let countriesGeoJSON: TCountriesGeoJSON;

export const loadCountriesGeoJSON = async (): Promise<TCountriesGeoJSON> => {
    if (countriesGeoJSON) {
        return countriesGeoJSON;
    }
    const response = await fetch(
        'https://sind-storage.ams3.cdn.digitaloceanspaces.com/global-map/countries_simplified_custom.json'
    );
    countriesGeoJSON = await response.json();
    return countriesGeoJSON;
};

import {
    aggregateHealthImpactCaused,
    aggregateIncidentsByPerpetrators,
    hasUrlParameters,
    setIncidentPopupAndCountryFromURL,
    setMapViewFromURL,
    updateUrlParameters,
    openIncidentPopupAndFetchDescription,
} from '@helpers';

let hoveredCountryId: string | number | undefined;
let hoveredFeatureId: string | number | undefined;

export const mapReference = { current: undefined as Map | undefined };

export const scrollToMap = (): void => {
    const mapSection = document.querySelector('#mapSection');
    if (!mapSection) return;

    // Watch for the map section entering the viewport, then enable scroll zoom.
    // This avoids a race condition where scrollZoom.enable() fires before the
    // smooth scroll animation has brought the map into view.
    const observer = new IntersectionObserver(
        (entries, obs) => {
            if (entries[0].isIntersecting) {
                mapReference.current?.scrollZoom.enable(); // Re-enable scroll zoom since map is the focus
                obs.disconnect(); // Clean up — we only need to fire once
            }
        },
        { threshold: 0.5 } // Trigger when at least 50% of the map is visible
    );

    observer.observe(mapSection);
    mapSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

async function getLargestCountryFeature(code) {
    const countriesGeoJson = await loadCountriesGeoJSON();
    // Filter all features matching the country code (there may be multiple
    // e.g. US mainland, Puerto Rico, Guam share the same ISO_A3_EH code)
    const features = countriesGeoJson.features.filter(
        f => f.properties.ISO_A3_EH === code
    );

    if (features.length === 0) return;

    // Skip reduction if there's only one match
    if (features.length === 1) return features[0];

    // Return the feature with the largest geodesic area using @turf/area,
    // which computes true surface area rather than a bounding box approximation
    return features.reduce((largest, feature) =>
        area(feature) > area(largest) ? feature : largest
    );
}

function addIncidentsPopupEvents(map: Map) {
    return async event => {
        const feature = event.features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const incidentID = String(feature.properties.incidentNumber);

        while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        openIncidentPopupAndFetchDescription(map, coordinates, incidentID);
    };
}

function getCountsForColumn(
    incidents: THealthSecurityIncident[],
    countryCode: string
) {
    return incidents.reduce((accumulator, currentValue) => {
        accumulator[currentValue[countryCode]] =
            (accumulator[currentValue[countryCode]] || 0) + 1;
        return accumulator;
    }, {});
}

const hoverCase = (defaultValue, hoverValue) => [
    'case',
    ['boolean', ['feature-state', 'hover'], false],
    hoverValue,
    defaultValue,
];

export async function aggregateCountryIncidentData(
    incidents: THealthSecurityIncident[],
    countryCode: string
): Promise<TSelectedCountry> {
    const countriesGeoJson = await loadCountriesGeoJSON();
    const country = countriesGeoJson.features.find(
        country => country.properties.ISO_A3_EH === countryCode
    );

    const incidentsForCountry = incidents.filter(
        incident => incident.isoCode === countryCode
    );

    const aggregateHealthImpacts = aggregateHealthImpactCaused(
        incidentsForCountry
    );

    const countryData = {
        country: country.properties.NAME_EN,
        isoCode: country.properties.ISO_A3_EH,
        totalIncidents: incidentsForCountry.length,
        healthFacilitiesDamagedDestroyed:
            aggregateHealthImpacts('totalHealthFacilitiesDamaged') +
            aggregateHealthImpacts('totalHealthFacilitiesDestroyed'),
        healthWorkersKilled: aggregateHealthImpacts('totalHealthWorkerKilled'),
        healthWorkersKidnapped: aggregateHealthImpacts(
            'totalHealthWorkerKidnapped'
        ),
        healthWorkersArrested: aggregateHealthImpacts(
            'totalHealthWorkerArrested'
        ),
        perpetrators: aggregateIncidentsByPerpetrators('perpetratorMap')(
            incidentsForCountry
        ),
        healthMeasureType: aggregateIncidentsByPerpetrators('healthMeasureMap')(
            incidentsForCountry
        ),
        weaponUse: aggregateIncidentsByPerpetrators('weaponUseMap')(
            incidentsForCountry
        ),
    };

    return countryData;
}

function handleMouseScrollOnFocus(map) {
    const mapContainer = map.getContainer();
    const mapParent = mapContainer.parentElement;

    // If the function is called in a state where url parameters are used
    // the scrollZoom should be enabled, since the map will be the focus
    if (hasUrlParameters) map.scrollZoom.enable();
    else map.scrollZoom.disable();

    const enableEvents = [
        'click',
        'dblclick',
        'contextmenu',
        'mousedown',
        'touchstart',
        'dragstart',
    ];
    for (const eventTrigger of enableEvents) {
        mapParent.addEventListener(
            eventTrigger,
            () => map.scrollZoom.enable(),
            { passive: true }
        );
    }

    const disableEvents = ['click', 'mousedown', 'touchstart', 'focusin'];
    for (const eventTrigger of disableEvents) {
        document.addEventListener(
            eventTrigger,
            event => {
                // Walk the composed path instead of checking current DOM containment.
                // This handles popups that remove themselves before the event bubbles,
                // and elements rendered in shadow DOMs.
                const path = event.composedPath();
                const insideMap =
                    path.includes(mapParent) || path.includes(mapContainer);
                if (!insideMap) map.scrollZoom.disable();
            },
            { passive: true }
        );
    }
}

export function initMap(
    mapContainer: HTMLDivElement,
    incidents: THealthSecurityIncident[],
    filterCategory: string,
    handleSelectCountry: (countryCode: string) => void,
    handleClearSelectedCountry: () => void,
    scrollZoomRequiresFocus: boolean,
    isSmallScreen: boolean,
    setMultipleCountries: (value: boolean) => void
) {
    const STARTING_BOUNDS = [
        [-124.7, -55.9], // SW: US west coast lng, Cape Horn lat
        [178.5, 69.1], // NE: NZ east coast lng, Northern Sweden lat
    ];

    const SMALL_SCREEN_PADDING = 0;
    const LARGE_SCREEN_PADDING = {
        top: 20,
        bottom: 20,
        left: 200,
        right: 300,
    };

    const map = new mapboxgl.Map({
        container: mapContainer,
        style: MAPBOX_BASEMAP_STYLE,
        bounds: STARTING_BOUNDS,
        fitBoundsOptions: {
            padding: isSmallScreen
                ? SMALL_SCREEN_PADDING
                : LARGE_SCREEN_PADDING,
        },
        attributionControl: false,
        maxZoom: 11,
        dragRotate: false,
    });
    map.touchZoomRotate.disableRotation();

    mapReference.current = map;

    // Handle logic if map does not start as scroll zoomable
    if (scrollZoomRequiresFocus) handleMouseScrollOnFocus(map);

    // Zoom and coordinates
    setMapViewFromURL(map);

    // Add countries, incidents and popups to map
    map.on('load', async () => {
        await addCountriesLayerToMap(map, incidents, filterCategory);
        addIncidentsLayersToMap(incidents, map, isSmallScreen);
        setIncidentPopupAndCountryFromURL(
            map,
            incidents,
            handleSelectCountry,
            isSmallScreen,
            setMultipleCountries
        );
    });

    // Prevents country click interaction in case user clicked an event
    let clickedIncident = false;
    map.on('click', LAYER_NAMES.POINTS, event => {
        addIncidentsPopupEvents(map)(event);
        clickedIncident = true;
    });

    map.on('click', LAYER_NAMES.COUNTRIES, function (event) {
        if (clickedIncident) clickedIncident = false;
        else {
            const countryCode = event?.features[0]?.properties?.ISO_A3_EH;
            handleSelectCountry(countryCode);
            setBoundsToCountry(map, countryCode, isSmallScreen);
        }
    });

    map.on('click', event => {
        // Check if the click event's target is within your layer
        const features = map.queryRenderedFeatures(event.point);
        const isInsideLayer = features.some(function (feature) {
            return feature.layer.id === LAYER_NAMES.COUNTRIES;
        });

        // If the click event is outside COUNTRIES layer
        if (!isInsideLayer) {
            // Clear selected country
            handleClearSelectedCountry();
        }
    });

    // Change the cursor to a pointer when the mouse is over the places layer.
    map.on('mouseenter', LAYER_NAMES.COUNTRIES, function () {
        map.getCanvas().style.cursor = 'pointer';
    });

    // Change it back to a grab when it leaves.
    map.on('mouseleave', LAYER_NAMES.COUNTRIES, function () {
        map.getCanvas().style.cursor = 'grab';
    });

    map.on('moveend', () => {
        const center = map.getCenter();
        const lat = center.lat.toFixed(5);
        const lng = center.lng.toFixed(5);
        const zoom = map.getZoom().toFixed(2);
        updateUrlParameters({ lat, lng, zoom });
    });

    map.on('remove', () => {
        mapReference.current = undefined; // clean up on unmount
    });

    return map;
}

export async function assignIncidentCountToCountry(
    incidents: THealthSecurityIncident[]
): Promise<void> {
    const incidentsCountPerCountry = getCountsForColumn(incidents, 'isoCode');
    const countriesGeoJson = await loadCountriesGeoJSON();
    const features = countriesGeoJson.features;

    for (const country of features) {
        country.properties.incidentsCount =
            incidentsCountPerCountry[country.properties.ISO_A3_EH] ?? 0;
    }
}

function isArrayofTwoNumbers(variable) {
    return (
        Array.isArray(variable) &&
        variable.length === 2 &&
        variable.every(number => typeof number === 'number')
    );
}

export async function setBoundsToCountry(
    map: Map,
    selectedCountryCode: string,
    isSmallScreen: boolean
): Promise<void> {
    if (selectedCountryCode && selectedCountryCode !== '') {
        const feature = await getLargestCountryFeature(selectedCountryCode);
        const bounds = new mapboxgl.LngLatBounds();

        // Normalize Polygon and MultiPolygon into the same shape for uniform iteration
        const polygons =
            feature.geometry.type === 'MultiPolygon'
                ? feature.geometry.coordinates // [polygon][ring][coord]
                : [feature.geometry.coordinates]; // wrap Polygon to match MultiPolygon shape

        // Iterate all polygons and their outer rings to build the full bounds
        for (const polygon of polygons) {
            for (const coordinateSet of polygon[0]) {
                if (isArrayofTwoNumbers(coordinateSet)) {
                    bounds.extend(coordinateSet);
                } else {
                    for (const subSet of coordinateSet) {
                        if (isArrayofTwoNumbers(subSet)) bounds.extend(subSet);
                    }
                }
            }
        }

        map.fitBounds(bounds, {
            padding: isSmallScreen
                ? { top: 80, bottom: 100, left: 30, right: 30 }
                : 140,
            offset: isSmallScreen ? [0, -50] : [0, 0],
        });
    }
}

const CATEGORY_COLORS = {
    Health: {
        baseColor: 'healthBlue',
    },
    Conflict: {
        baseColor: 'conflictRed',
    },
    Outbreak: {
        baseColor: 'outbreakGreen',
    },
    Ebola: {
        baseColor: 'ebolaRed',
    },
    Political: {
        baseColor: 'politicalOrange',
    },
    Vaccination: {
        baseColor: 'vaccinationGold',
    },
    MaternalCare: {
        baseColor: 'maternalPurple',
    },
    DroneImpact: {
        baseColor: 'droneBrown',
    },
};

// Ensures step values are unique by slightly decreasing earlier duplicates
const handleDuplicateSteps = (stepsArray: number[]): number[] => {
    const uniqueSteps = [...stepsArray];
    const numberOfSteps = uniqueSteps.length;
    for (let index = 1; index < numberOfSteps; ++index) {
        if (uniqueSteps[index] === uniqueSteps[index - 1]) {
            // Offset earlier value so duplicate maps to a darker shade
            const differentiator = (numberOfSteps - index) * 0.01;
            uniqueSteps[index - 1] -= differentiator;
        }
    }
    return uniqueSteps;
};

// Calculates the quartile value for a given locator in an ordered array
const calculateQuartileValue = (
    locator: number,
    orderedArray: number[]
): number => {
    const index = locator - 1;
    return index % 1 === 0
        ? (orderedArray[index] + orderedArray[index + 1]) / 2 // Average neighbors for exact index
        : orderedArray[Math.ceil(index)]; // Round up for fractional index
};

// Computes first, second (median), and third quartiles from an ordered array
const calculateQuartiles = (orderedArray: number[]): number[] => {
    const incidentCountsNumber = orderedArray.length;

    const quartileLocators = [0.25, 0.5, 0.75];
    const quartileValues = quartileLocators.map(locator => {
        return calculateQuartileValue(
            incidentCountsNumber * locator,
            orderedArray
        );
    });
    return quartileValues;
};

// Extracts unique values from an object and returns them sorted in ascending order
const getUniqueSortedValues = (
    incidentsCount: Record<string, number>
): number[] => {
    const countValues = Object.values(incidentsCount);
    const uniqueValues = new Set(countValues);
    const uniqueSortedValues = [...uniqueValues].sort((a, b) => a - b);
    return uniqueSortedValues;
};

// Builds quartiles from incident counts per country
const getCountriesQuartileValues = (
    incidents: THealthSecurityIncident[]
): number[] => {
    // Recount incidents to ensure accurate data for the current category
    const incidentsCountPerCountry = getCountsForColumn(incidents, 'isoCode');

    const uniqueSortedIncidentCounts = getUniqueSortedValues(
        incidentsCountPerCountry
    );
    // Return default values in case of no incidents
    if (uniqueSortedIncidentCounts.length === 0) {
        return [2, 3, 4];
    }

    return calculateQuartiles(uniqueSortedIncidentCounts);
};

// Generates color steps using quartiles and avoids duplicate thresholds
const createCategoryQuartileColorSteps = (
    baseColor: string,
    incidents: THealthSecurityIncident[]
) => {
    const [q1Value, q2Value, q3Value] = getCountriesQuartileValues(incidents);

    const stepValues = [0, 1, q1Value, q2Value, q3Value];
    const parsedStepValues = handleDuplicateSteps(stepValues);

    const colorShades = [
        'white',
        `${baseColor}25`,
        `${baseColor}50`,
        `${baseColor}75`,
        baseColor,
    ];

    return parsedStepValues.map((value, index) => ({
        VALUE: value,
        COLOUR: colorShades[index],
    }));
};

export function setCountryStyles(
    map: Map,
    filterCategory: string,
    incidents: THealthSecurityIncident[],
    selectedCountryCode?: string
): void {
    const countryCountQuartilesSteps = createCategoryQuartileColorSteps(
        CATEGORY_COLORS[filterCategory].baseColor,
        incidents
    );

    const filterStyles = countryCountQuartilesSteps.flatMap(style => [
        style.VALUE,
        defaultTheme.palette.colors[style.COLOUR],
    ]);

    filterStyles.shift();
    if (selectedCountryCode && selectedCountryCode !== '') {
        map.setPaintProperty(LAYER_NAMES.COUNTRIES, 'fill-color', [
            'match',
            ['get', 'ISO_A3_EH'],
            selectedCountryCode,
            'rgba(0, 0, 0, 0)',
            'DarkGray',
        ]);
    } else {
        map.setPaintProperty(LAYER_NAMES.COUNTRIES, 'fill-color', [
            ...STEP_STYLE,
            ...filterStyles,
        ]);
    }
}

async function addCountriesLayerToMap(
    map: Map,
    incidents: THealthSecurityIncident[],
    filterCategory: string
) {
    const countriesGeoJson = await loadCountriesGeoJSON();
    await assignIncidentCountToCountry(incidents);

    // Load country dataset
    map.addSource(LAYER_SOURCE_NAMES.COUNTRIES, {
        type: 'geojson',
        data: countriesGeoJson,
        generateId: true,
    });

    map.addLayer(
        {
            id: LAYER_NAMES.COUNTRIES,
            source: LAYER_SOURCE_NAMES.COUNTRIES,
            type: 'fill',
            paint: {
                'fill-outline-color': 'white',
            },
        },
        'poi-label'
    );
    setCountryStyles(map, filterCategory, incidents);

    map.addLayer(
        {
            id: LAYER_NAMES.COUNTRIES_HOVER,
            source: LAYER_SOURCE_NAMES.COUNTRIES,
            type: 'fill',
            paint: {
                'fill-color': 'rgba(255, 255, 255, 0.3)',
                'fill-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    1,
                    0,
                ],
            },
        },
        'poi-label'
    );

    map.addLayer(
        {
            id: LAYER_NAMES.COUNTRIES_HOVER_OUTLINE,
            source: LAYER_SOURCE_NAMES.COUNTRIES,
            type: 'line',
            paint: {
                'line-color': 'rgba(255, 255, 255, 0.9)',
                'line-width': 2,
                'line-opacity': [
                    'case',
                    ['boolean', ['feature-state', 'hover'], false],
                    1,
                    0,
                ],
            },
        },
        'poi-label'
    );

    map.on('mousemove', LAYER_NAMES.COUNTRIES, event => {
        if (event.features.length === 0) return;

        if (hoveredCountryId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.COUNTRIES, id: hoveredCountryId },
                { hover: false }
            );
        }

        hoveredCountryId = event.features[0].id;
        // Only update hover state if country has a valid ID
        if (hoveredCountryId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.COUNTRIES, id: hoveredCountryId },
                { hover: true }
            );
        }
    });

    map.on('mouseleave', LAYER_NAMES.COUNTRIES, () => {
        if (hoveredCountryId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.COUNTRIES, id: hoveredCountryId },
                { hover: false }
            );
        }

        hoveredCountryId = undefined;
    });
}

export function updateIncidentLayers(
    map: Map,
    incidents: THealthSecurityIncident[]
) {
    map.getSource(LAYER_SOURCE_NAMES.POINTS).setData(
        createIncidentsGeoJson(incidents)
    );
}

export async function updateCountriesLayer(
    map: Map,
    incidents: THealthSecurityIncident[]
) {
    await assignIncidentCountToCountry(incidents);
    map.getSource(LAYER_SOURCE_NAMES.COUNTRIES).setData(countriesGeoJSON);
}

export function addIncidentsLayersToMap(
    incidents: THealthSecurityIncident[],
    map: Map,
    isSmallScreen: boolean
): void {
    map.addSource(LAYER_SOURCE_NAMES.POINTS, {
        type: 'geojson',
        data: createIncidentsGeoJson(incidents),
        generateId: true,
    });

    map.addLayer(
        {
            id: LAYER_NAMES.POINTS,
            source: LAYER_SOURCE_NAMES.POINTS,
            type: 'circle',
            paint: {
                'circle-radius': [
                    'interpolate',
                    ['exponential', 1.5],
                    ['zoom'],
                    1.5, // Zoom level at which circle radius is min (zoomed out)
                    4, // Min circle radius
                    isSmallScreen ? 4 : map.getMaxZoom(), // Zoom level at which circle radius is max
                    9, // Max circle radius
                ],
                'circle-color': hoverCase(
                    INCIDENTS_ICON.COLOUR,
                    INCIDENTS_ICON.HOVER_COLOUR
                ),
                'circle-stroke-color': hoverCase(
                    INCIDENTS_ICON.BORDER_COLOUR,
                    INCIDENTS_ICON.HOVER_BORDER_COLOUR
                ),
                'circle-stroke-width': hoverCase(
                    INCIDENTS_ICON.STROKE_WIDTH,
                    INCIDENTS_ICON.HOVER_STROKE_WIDTH
                ),
                'circle-opacity': hoverCase(
                    INCIDENTS_ICON.OPACITY,
                    INCIDENTS_ICON.HOVER_OPACITY
                ),
                'circle-stroke-opacity': hoverCase(
                    INCIDENTS_ICON.STROKE_OPACITY,
                    INCIDENTS_ICON.STROKE_WIDTH
                ),
            },
        },
        'poi-label'
    );

    map.on('mousemove', LAYER_NAMES.POINTS, event => {
        if (event.features.length === 0) return;

        // Clear country hover when moving over an incident
        if (hoveredCountryId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.COUNTRIES, id: hoveredCountryId },
                { hover: false }
            );
        }

        if (hoveredFeatureId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.POINTS, id: hoveredFeatureId },
                { hover: false }
            );
        }

        hoveredFeatureId = event.features[0].id;
        // Only update hover state if feature has a valid ID
        if (hoveredFeatureId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.POINTS, id: hoveredFeatureId },
                { hover: true }
            );
        }
    });

    map.on('mouseleave', LAYER_NAMES.POINTS, () => {
        if (hoveredFeatureId) {
            map.setFeatureState(
                { source: LAYER_SOURCE_NAMES.POINTS, id: hoveredFeatureId },
                { hover: false }
            );
        }

        hoveredFeatureId = undefined;
    });
}

export function setVisibleIncidentsInMap(
    map: Map,
    selectedCountryCode?: string
): void {
    if (!map.getLayer(LAYER_NAMES.POINTS)) {
        return;
    }
    if (selectedCountryCode && selectedCountryCode !== '') {
        map.setFilter(LAYER_NAMES.POINTS, [
            '==',
            ['get', 'countryCode'],
            selectedCountryCode,
        ]);
    } else {
        map.setFilter(LAYER_NAMES.POINTS);
    }
}

export function getFilterColumn(category: string): string {
    switch (category) {
        case 'Conflict':
            return DATA_VALUES.CONFLICT;
        case 'Political':
            return DATA_VALUES.POLITICAL;
        case 'Vaccination':
            return DATA_VALUES.VACCINATION;
        case 'Outbreak':
            return DATA_VALUES.OUTBREAK;
        case 'Ebola':
            return DATA_VALUES.EBOLA;
        case 'MaternalCare':
            return DATA_VALUES.MATERNAL_CARE;
        case 'DroneImpact':
            return DATA_VALUES.DRONE_IMPACT;
        default:
            return DATA_VALUES.HEALTH;
    }
}

function createIncidentsGeoJson(
    incidents: THealthSecurityIncident[]
): TIncidentsGeoJson {
    return {
        type: 'FeatureCollection',
        features: incidents
            .map(incident => {
                if (incident.latitude && incident.longitude) {
                    return {
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [
                                Number(incident.longitude.replace(/ /g, '')),
                                Number(incident.latitude.replace(/ /g, '')),
                            ],
                        },
                        properties: {
                            incidentNumber: incident.incidentNumber,
                            isoDate: incident.isoDate,
                            countryCode: incident.isoCode,
                        },
                    };
                }
            })
            .filter(Boolean),
    };
}
